import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';

export interface MaskCanvasHandle {
  /** Export the painted mask as a same-dimension B/W PNG blob (white = the region to change), or null if nothing painted. */
  exportMask: () => Promise<Blob | null>;
  clear: () => void;
  hasPaint: () => boolean;
  /** Undo the last stroke (restores the snapshot taken before it). Returns true if anything was undone. */
  undo: () => boolean;
  canUndo: () => boolean;
}

interface Props {
  /** Display URL of the source image (drawn as the backdrop the user paints over). */
  imageUrl: string;
  /** Brush radius in DISPLAYED pixels (scaled to the natural image resolution when painting). */
  brushSize?: number;
  /** Erase mode — the brush REMOVES paint (the "select less" gesture) instead of adding it. */
  erase?: boolean;
  onPaintChange?: (hasPaint: boolean) => void;
  /** Bubbled so the panel can enable/disable Undo. */
  onHistoryChange?: (canUndo: boolean) => void;
}

/** Cap the undo history so a long editing session can't grow memory unbounded (bounded accumulator). */
const MAX_HISTORY = 20;

/**
 * MaskCanvas — paint a region over an image to mark WHAT to change (the Firefly/Canva "generative fill" gesture).
 * The user brushes; we keep the mask at the image's NATURAL resolution (so the exported mask matches the source
 * dimensions FLUX Fill requires). White strokes on a black field = the region to regenerate. Mouse + touch.
 * Refinements: adjustable brush size, an ERASER (select-less), and per-stroke UNDO (bounded history). The parent
 * drives brush size + erase mode via props and triggers export/undo/clear via the ref.
 */
const MaskCanvas = forwardRef<MaskCanvasHandle, Props>(({ imageUrl, brushSize = 28, erase = false, onPaintChange, onHistoryChange }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // Natural (source) dimensions — the mask is authored at this resolution so it matches the source 1:1.
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const drawing = useRef(false);
  const painted = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  // Per-stroke snapshots for undo — captured at the START of each stroke (the state to restore TO).
  const history = useRef<ImageData[]>([]);
  const eraseRef = useRef(erase);
  eraseRef.current = erase;

  // Keep the callbacks in refs so the imperative handle + event handlers stay stable (no stale closures, no re-bind).
  const onHistoryRef = useRef(onHistoryChange);
  onHistoryRef.current = onHistoryChange;
  const onPaintRef = useRef(onPaintChange);
  onPaintRef.current = onPaintChange;
  const emitHistory = () => onHistoryRef.current?.(history.current.length > 0);
  const emitPaint = (v: boolean) => onPaintRef.current?.(v);

  // Size the canvas to the image's natural resolution once it loads (mask must match source dims).
  const onLoad = () => {
    const img = imgRef.current; if (!img) return;
    setDims({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
  };

  useEffect(() => {
    const c = canvasRef.current; if (!c || !dims) return;
    c.width = dims.w; c.height = dims.h;
    const ctx = c.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height); // transparent overlay; strokes render as a translucent highlight
    history.current = []; painted.current = false; emitHistory();
  }, [dims]);

  // Map a pointer event (in displayed CSS pixels) to the canvas's natural-resolution coordinates.
  const toCanvas = (clientX: number, clientY: number) => {
    const c = canvasRef.current; if (!c) return { x: 0, y: 0, scale: 1 };
    const rect = c.getBoundingClientRect();
    const scale = c.width / rect.width; // natural px per displayed px
    return { x: (clientX - rect.left) * scale, y: (clientY - rect.top) * scale, scale };
  };

  const stroke = (clientX: number, clientY: number) => {
    const c = canvasRef.current; const ctx = c?.getContext('2d'); if (!c || !ctx) return;
    const { x, y, scale } = toCanvas(clientX, clientY);
    const r = brushSize * scale; // scale the display brush to natural pixels
    // Erase = punch holes in the overlay (destination-out); paint = add the translucent white highlight.
    ctx.globalCompositeOperation = eraseRef.current ? 'destination-out' : 'source-over';
    ctx.strokeStyle = 'rgba(255,255,255,1)'; ctx.fillStyle = 'rgba(255,255,255,0.55)';
    if (eraseRef.current) { ctx.strokeStyle = 'rgba(0,0,0,1)'; ctx.fillStyle = 'rgba(0,0,0,1)'; }
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = r * 2;
    ctx.beginPath();
    if (last.current) { ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(x, y); ctx.stroke(); }
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    last.current = { x, y };
    if (!eraseRef.current && !painted.current) { painted.current = true; emitPaint(true); }
  };

  const start = (e: React.PointerEvent) => {
    const c = canvasRef.current; const ctx = c?.getContext('2d'); if (!c || !ctx) return;
    // Snapshot BEFORE the stroke so undo restores the pre-stroke state. Bounded FIFO.
    history.current.push(ctx.getImageData(0, 0, c.width, c.height));
    if (history.current.length > MAX_HISTORY) history.current.shift();
    emitHistory();
    drawing.current = true; last.current = null; stroke(e.clientX, e.clientY);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const move = (e: React.PointerEvent) => { if (drawing.current) { e.preventDefault(); stroke(e.clientX, e.clientY); } };
  const end = () => {
    drawing.current = false; last.current = null;
    // Recompute "has paint" after an erase stroke could have cleared everything.
    const c = canvasRef.current; const ctx = c?.getContext('2d');
    if (c && ctx) {
      const data = ctx.getImageData(0, 0, c.width, c.height).data;
      let any = false;
      for (let i = 3; i < data.length; i += 4) { if (data[i] > 10) { any = true; break; } }
      if (any !== painted.current) { painted.current = any; emitPaint(any); }
    }
  };

  useImperativeHandle(ref, () => ({
    hasPaint: () => painted.current,
    canUndo: () => history.current.length > 0,
    undo: () => {
      const c = canvasRef.current; const ctx = c?.getContext('2d');
      const snap = history.current.pop();
      if (!c || !ctx || !snap) return false;
      ctx.putImageData(snap, 0, 0);
      emitHistory();
      // Re-derive paint state from the restored snapshot.
      let any = false;
      for (let i = 3; i < snap.data.length; i += 4) { if (snap.data[i] > 10) { any = true; break; } }
      if (any !== painted.current) { painted.current = any; emitPaint(any); }
      return true;
    },
    clear: () => {
      const c = canvasRef.current; const ctx = c?.getContext('2d');
      if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
      history.current = []; painted.current = false; emitPaint(false); emitHistory();
    },
    // Flatten the translucent overlay to a hard B/W mask (white where painted, black elsewhere) at natural res.
    exportMask: async () => {
      const c = canvasRef.current; if (!c || !painted.current) return null;
      const out = document.createElement('canvas'); out.width = c.width; out.height = c.height;
      const octx = out.getContext('2d'); if (!octx) return null;
      octx.fillStyle = '#000'; octx.fillRect(0, 0, out.width, out.height); // keep = black
      const src = c.getContext('2d')?.getImageData(0, 0, c.width, c.height);
      if (!src) return null;
      const dst = octx.getImageData(0, 0, out.width, out.height);
      for (let i = 0; i < src.data.length; i += 4) {
        if (src.data[i + 3] > 10) { dst.data[i] = 255; dst.data[i + 1] = 255; dst.data[i + 2] = 255; dst.data[i + 3] = 255; } // painted → white (change)
      }
      octx.putImageData(dst, 0, 0);
      return await new Promise<Blob | null>((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
    },
  }), []); // callbacks are read via refs, so the handle is stable

  return (
    <div className="relative inline-block max-h-[46dvh] overflow-hidden rounded-xl">
      <img ref={imgRef} src={imageUrl} alt="Paint the area to change" onLoad={onLoad} draggable={false} className="block max-h-[46dvh] w-auto select-none" />
      <canvas
        ref={canvasRef}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        className={`absolute inset-0 h-full w-full touch-none ${erase ? 'cursor-cell' : 'cursor-crosshair'}`}
      />
    </div>
  );
});

MaskCanvas.displayName = 'MaskCanvas';
export default MaskCanvas;
